/**
 * @license Copyright (c) 2003-2019, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import { ClassicEditor as ClassicEditorBase, Style } from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'

import { Essentials } from '@ckeditor/ckeditor5-essentials'
import { CKFinderUploadAdapter as UploadAdapter } from '@ckeditor/ckeditor5-adapter-ckfinder'
import { Autoformat } from '@ckeditor/ckeditor5-autoformat'
import { Bold, Italic } from '@ckeditor/ckeditor5-basic-styles'
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote'
import { CKFinder } from '@ckeditor/ckeditor5-ckfinder'
import { EasyImage } from '@ckeditor/ckeditor5-easy-image'
import {
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
} from '@ckeditor/ckeditor5-image'
import { Heading } from '@ckeditor/ckeditor5-heading'
import { Link, LinkImage } from '@ckeditor/ckeditor5-link'
import { List } from '@ckeditor/ckeditor5-list'
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed'
import { Paragraph } from '@ckeditor/ckeditor5-paragraph'
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office'
import { Table, TableToolbar } from '@ckeditor/ckeditor5-table'
import { Highlight } from '@ckeditor/ckeditor5-highlight'
import ToolbarMenu from './plugins/toolbar-menu/toolbar-menu'
import RawHtml from './plugins/raw-html/raw-html'
import EmbedCode from './plugins/embed-code/embed-code'
import Callout from './plugins/callout/callout'
import StatInsight from './plugins/stat-insight/stat-insight'
import ExpertTip from './plugins/expert-tip/expert-tip'
import Resources from './plugins/resources/resources'
import Copyable from './plugins/copyable/copyable'
import Comparison from './plugins/comparison/comparison'
import InternalLink from './plugins/internal-link/internal-link'
import RelatedArticle from './plugins/related-article/related-article'
import RelatedExample from './plugins/related-example/related-example'
import TemplateSlider from './plugins/template-slider/template-slider'
import CompareResumes from './plugins/compare-resumes/compare-resumes'
import imageBorderIcon from './icons/image-border.svg'
import largeImageIcon from './icons/large-image.svg'
import Skills from './plugins/skills/skills'
import Salaries from './plugins/salaries/salaries'
import ResumeExample from './plugins/resume-example/resume-example'

// added on v26
import { CloudServices as CloudServicesPlugin } from '@ckeditor/ckeditor5-cloud-services'

// // CKEditor 5 v48+ (new installation methods) ships pre-built CSS instead of
// // compiling the theme from source. Import each used package's compiled
// // stylesheet so the default `.ck-*` classes and lark theme are bundled.
// import '@ckeditor/ckeditor5-ui/dist/index.css'
// import '@ckeditor/ckeditor5-widget/dist/index.css'
// import '@ckeditor/ckeditor5-editor-classic/dist/index.css'
// import '@ckeditor/ckeditor5-basic-styles/dist/index.css'
// import '@ckeditor/ckeditor5-block-quote/dist/index.css'
// import '@ckeditor/ckeditor5-heading/dist/index.css'
// import '@ckeditor/ckeditor5-highlight/dist/index.css'
// import '@ckeditor/ckeditor5-image/dist/index.css'
// import '@ckeditor/ckeditor5-link/dist/index.css'
// import '@ckeditor/ckeditor5-list/dist/index.css'
// import '@ckeditor/ckeditor5-media-embed/dist/index.css'
// import '@ckeditor/ckeditor5-table/dist/index.css'

import './styles.css'

export default class ClassicEditor extends ClassicEditorBase {}

// Plugins to include in the build.
ClassicEditor.builtinPlugins = [
  Essentials,
  UploadAdapter,
  Autoformat,
  Bold,
  Italic,
  BlockQuote,
  CKFinder,
  EasyImage,
  Heading,
  Image,
  ImageCaption,
  LinkImage,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  Highlight,
  ToolbarMenu,
  RawHtml,
  EmbedCode,
  Callout,
  StatInsight,
  Copyable,
  ExpertTip,
  Resources,
  Comparison,
  InternalLink,
  RelatedArticle,
  RelatedExample,
  TemplateSlider,
  CompareResumes,
  Skills,
  Salaries,
  ResumeExample,
  CloudServicesPlugin,
  Style,
]

// Editor configuration.
ClassicEditor.defaultConfig = {
  // Required since CKEditor 5 v44. Commercial license key (contract signed
  // 2026-07-15); CKEditor keys are domain/build-validated and ship in the
  // client bundle by design. Consumers may override via editor config.
  licenseKey:
    'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MTU2OTU5OTksImp0aSI6IjMwMjYxOTEwLTYwMTUtNGUzZC05MDE2LWEzZmVkMTBmNjk1YiIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIkRPIiwiRlAiLCJTQyIsIlRPQyIsIlRQTCIsIlBPRSIsIkNDIiwiTUYiLCJTRUUiLCJFQ0giLCJFSVMiLCJMSCIsIkZPTyJdLCJ2YyI6IjQwMGM3ZmUwIn0.fiqRb4fi4JyQwBn5WM2pFlsbqGSKueAVU6OgFIjcsBhEvElsbB5_WbMPcMcB-VyKZA3GVO0eCBD1Ly5pjMKvbg',
  toolbar: {
    items: [
      'heading',
      '|',
      'bold',
      'italic',
      'internalLink',
      'link',
      'linkImage',
      'bulletedList',
      'numberedList',
      'imageUpload',
      'blockQuote',
      'insertTable',
      'mediaEmbed',
      'highlight',
      'toolbarMenu',
    ],
  },
  image: {
    toolbar: [
      'imageStyle:default',
      'imageStyle:large',
      '|',
      'imageStyle:bordered',
      '|',
      'imageTextAlternative',
    ],
    styles: {
      options: [
        {
          name: 'default',
          icon: 'full',
          title: 'Default image',
          isDefault: true,
          // Image types (names of the model elements) supporting this style.
          modelElements: ['imageBlock', 'imageInline'],
        },
        {
          name: 'large',
          icon: largeImageIcon,
          title: 'Large image',
          className: 'image--large',
          // Image types (names of the model elements) supporting this style.
          modelElements: ['imageBlock', 'imageInline'],
        },
        {
          name: 'bordered',
          icon: imageBorderIcon,
          title: 'Bordered image',
          className: 'image--bordered',
          // Image types (names of the model elements) supporting this style.
          modelElements: ['imageBlock', 'imageInline'],
        },
      ],
    },
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      {
        model: 'heading1',
        view: 'h2',
        title: 'H2',
        class: 'ck-heading_heading1',
      },
      {
        model: 'heading2',
        view: 'h3',
        title: 'H3',
        class: 'ck-heading_heading2',
      },
      {
        model: 'heading3',
        view: 'h4',
        title: 'H4',
        class: 'ck-heading_heading3',
      },
      {
        model: 'chapterLabel',
        view: {
          name: 'div',
          classes: 'chapter-label',
        },
        title: 'Chapter label',
        class: 'ck-heading_chapter-label',
        converterPriority: 'high',
      },
      {
        model: 'chapterHeading',
        view: {
          name: 'h2',
          classes: 'chapter-heading',
        },
        title: 'Chapter Heading',
        class: 'ck-heading_chapter-heading',
        converterPriority: 'high',
      },
    ],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
  highlight: {
    options: [
      {
        model: 'yellowMarker',
        class: 'marker-yellow',
        title: 'Yellow marker',
        color: 'var(--ck-highlight-marker-yellow)',
        type: 'marker',
      },
    ],
  },
  // This value must be kept in sync with the language defined in webpack.config.js.
  language: 'en',
}
